import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';
import useMediaQuery from '@loomstateorg/shared-frontend/hooks/useMediaQuery';
import { desktop } from '@loomstateorg/shared-frontend/utils/screensizes';

export interface CheckboxProps extends Omit<React.ComponentProps<'input'>, 'onChange'> {
  label?: string;
  labelPosition?: 'on-left' | 'on-right';
  containerClassName?: string;
  onChange?: (props: { name: string; checked: boolean }) => void;
  disableBackground?: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({
  className = '',
  checked,
  onChange,
  onMouseOver,
  onMouseLeave,
  containerClassName,
  label,
  defaultChecked = false,
  labelPosition = 'on-right',
  disableBackground = false,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isChecked, setIsChecked] = useState(checked ?? defaultChecked ?? false);

  const [isDesktopSize] = useMediaQuery(desktop);

  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (typeof checked !== 'undefined') setIsChecked(checked);
  }, [checked]);

  const handleContainerClick = () => {
    if (!isDesktopSize) {
      toggleIsChecked();
    }
  };

  const toggleIsChecked = useCallback(() => {
    onChange?.({ name: props.name ?? '', checked: !isChecked });
    setIsChecked(!isChecked);
  }, [isChecked, onChange, props.name]);

  const handleMouseOver = (e: React.MouseEvent<HTMLInputElement>) => {
    setIsHovered(isDesktopSize);
    onMouseOver?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLInputElement>) => {
    setIsHovered(false);
    onMouseLeave?.(e);
  };

  const containerClassNames = useClassNames(['flex items-center gap-12', containerClassName]);
  const buttonClassName = useClassNames([
    'h-20 w-20 min-w-[20px] rounded-sm relative outline outline-1 cursor-pointer',
    isHovered ? 'outline-primary-regular' : 'outline-neutral-400',
  ]);

  const inputClassName = useClassNames([
    'absolute w-full h-full z-[2] checked:bg-none bg-transparent text-transparent border-transparent',
    className,
  ]);

  const iconClassName = useClassNames([
    'absolute top-[50%] z-[1] left-[50%] h-16 w-16 translate-y-[-50%] translate-x-[-50%] stroke-[2px]',
    isChecked || isHovered ? 'block' : 'hidden',
    isChecked && !disableBackground ? 'text-white' : 'text-primary-regular',
  ]);

  const backgroundClassName = useClassNames([
    'absolute z-0 h-20 w-20 rounded-sm',
    { 'bg-secondary-regular': !!isChecked },
  ]);

  const { id, ...rest } = props;
  const inputId = id ?? (label ? `atom-checkbox-${props.name ?? 'input'}` : undefined);

  const LabelElement = label ? (
    <label htmlFor={inputId} className="text-14 text-primary-regular" data-testid="atom-checkbox-label">
      {label}
    </label>
  ) : null;

  return (
    <div className={containerClassNames} onClick={handleContainerClick} data-testid="atom-checkbox">
      {label && labelPosition === 'on-left' && LabelElement}

      <div className={buttonClassName}>
        <input
          ref={checkboxRef}
          id={inputId}
          type="checkbox"
          checked={isChecked}
          className={inputClassName}
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          onChange={toggleIsChecked}
          {...rest}
          data-testid={(props as { 'data-testid'?: string })['data-testid'] ?? 'atom-checkbox-input'}
        />

        <CheckIcon className={iconClassName} aria-hidden="true" />

        {!disableBackground && <div className={backgroundClassName} />}
      </div>

      {label && labelPosition === 'on-right' && LabelElement}
    </div>
  );
};

export default Checkbox;